import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, array, boolean, object, string } from 'yup'

import {
  AutoEnrollmentDefaultConditionsModel,
  AutoEnrollmentModel,
} from '@/AutoEnrollment/domain'
import { documentType } from '@/Shared/domain'
import {
  documentNumberSchema,
  documentTypeSchema,
  emailSchema,
  mobilePhoneSchema,
  nonEmptyPositiveNumberSchema,
  nonEmptyStringSchema,
  stringSchema,
} from '@/Shared/ui/form'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

export type Step1ProductServiceForm = {
  isActive: boolean
  serviceId: number
}

export type Step1ProductForm = {
  productId: number
  productServices?: Step1ProductServiceForm[]
}

export interface Step1Form {
  products: Step1ProductForm[]
}

const step1Schema = (t: TFunction<'translation', undefined>): ObjectSchema<Step1Form> => {
  return object({
    products: array()
      .of(
        object({
          productId: nonEmptyPositiveNumberSchema(t),
          productServices: array()
            .of(
              object({
                isActive: boolean().default(false),
                serviceId: nonEmptyPositiveNumberSchema(t),
              }),
            )
            .optional(),
        }),
      )
      .required(t(formTranslation.errors.fieldRequired)),
  })
}

export interface Step2Form {
  name: string
  surname1?: string
  documentType?: documentType
  documentNumber: string
  email: string
  phone?: string
  isEditable: boolean
}

const step2Schema = (t: TFunction<'translation', undefined>): ObjectSchema<Step2Form> => {
  return object({
    documentType: documentTypeSchema(t, true) as any,
    documentNumber: documentNumberSchema(t, true, 'documentType').required(
      t(formTranslation.errors.fieldRequired),
    ),
    name: nonEmptyStringSchema(t, 20),
    surname1: string().when('isEditable', (isEditable: boolean[]) => {
      if (isEditable[0]) {
        return nonEmptyStringSchema(t, 41)
      } else {
        return nonEmptyStringSchema(t, 255)
      }
    }),
    email: emailSchema(t).required(t(formTranslation.errors.fieldRequired)),
    phone: string().when('isEditable', (isEditable: boolean[]) => {
      if (isEditable[0]) {
        return mobilePhoneSchema(t).required(t(formTranslation.errors.fieldRequired))
      } else {
        return stringSchema(t, 20)
      }
    }),
    isEditable: boolean().default(false),
  })
}

export type FormReturnType = UseFormReturn<Step1Form> | UseFormReturn<Step2Form>

interface IUseAutoEnrollmentFormManager {
  getCurrentForm: (step: number) => FormReturnType
  initForms: (
    availableProducts: AutoEnrollmentDefaultConditionsModel[],
    autoEnrollmentData: AutoEnrollmentModel,
  ) => Promise<void>
}

export const useProductContractingFormManager = (): IUseAutoEnrollmentFormManager => {
  const { t } = useTranslation()

  const step1Form = useForm<Step1Form>({
    resolver: yupResolver(step1Schema(t)),
    mode: 'onChange',
  })

  const initStep1Form = (
    availableProducts: AutoEnrollmentDefaultConditionsModel[],
  ): void => {
    const cleanedProducts = availableProducts.map(product => ({
      productId: product.productCode,
      productServices: product.subProducts?.map(subProduct => ({
        isActive: false,
        serviceId: subProduct.id,
      })),
    }))

    step1Form.reset({
      ...step1Form.getValues(),
      products: cleanedProducts,
    })
  }

  const step2Form = useForm<Step2Form>({
    resolver: yupResolver(step2Schema(t)),
    mode: 'onChange',
    defaultValues: {
      documentType: undefined,
      documentNumber: '',
      name: '',
      surname1: '',
      email: '',
      phone: '',
      isEditable: false,
    },
  })

  const initStep2Form = (autoEnrollmentData: AutoEnrollmentModel): void => {
    // Siempre inicializar con undefined para mostrar el placeholder
    step2Form.reset({
      documentType: autoEnrollmentData.billingOwnerData.documentType ||undefined,
      documentNumber: autoEnrollmentData.billingOwnerData.documentNumber || '',
      name: autoEnrollmentData.billingOwnerData.name || '',
      surname1: autoEnrollmentData.billingOwnerData.surname1,
      email: autoEnrollmentData.billingOwnerData.email || '',
      phone: autoEnrollmentData.billingOwnerData.phone || '',
      isEditable: autoEnrollmentData.billingOwnerData.isEditable || false,
    })
  }

  const getCurrentForm = (step: number): FormReturnType => {
    switch (step) {
      case 1:
        return step1Form
      case 2:
        return step2Form
      default:
        return step1Form
    }
  }

  const initForms = async (
    availableProducts: AutoEnrollmentDefaultConditionsModel[],
    autoEnrollmentData: AutoEnrollmentModel,
  ): Promise<void> => {
    initStep1Form(availableProducts)
    initStep2Form(autoEnrollmentData)
  }

  return {
    getCurrentForm,
    initForms,
  }
}
